import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';


const TransactionCard = ({ transaction, onRefresh }) => {
    const dispatch = useDispatch();
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [isRefunding, setIsRefunding] = useState(false);
    const [notification, setNotification] = useState(null);
    
    // Auto-dismiss notification after 5 seconds
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        let adjustedDateString = dateString;
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('T')) {
            adjustedDateString = dateString + 'Z';
        } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
            adjustedDateString = dateString + 'Z';
        }
        
        const date = new Date(adjustedDateString);
        
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Kolkata'
        }).toUpperCase().replace(/ /g, ', ');
    };

    const formatTime = (dateString) => {
        let adjustedDateString = dateString;
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('T')) {
            adjustedDateString = dateString + 'Z';
        } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
            adjustedDateString = dateString + 'Z';
        }
        
        const date = new Date(adjustedDateString);
        
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        }).toUpperCase();
    };

    const getStatusClass = (status) => {
        const statusLower = status?.toLowerCase();
        switch(statusLower) {
            case 'success':
            case 'successful':
            case 'completed':
                return 'bg-green-500 text-black';
            case 'pending':
            case 'processing':
                return 'bg-yellow-500 text-black';
            case 'failed':
            case 'failure':
                return 'bg-red-500 text-white';
            default:
                return 'bg-gray-500 text-white';
        }
    };

    const getEntryTypeClass = (entryType) => {
        const typeLower = entryType?.toLowerCase();
        if (typeLower?.includes('credit')) {
            return 'text-green-400';
        } else if (typeLower?.includes('debit')) {
            return 'text-red-400';
        }
        return 'text-zinc-400';
    };

    const getTypeLabel = (type) => {
        const typeMap = {
            'zave_reward_deposit': 'Zave Reward',
            'upi_instant_refund': 'UPI Refund',
            'upi_instant_deposit': 'UPI Deposit',
            'voucher_card_settlement': 'Voucher Settlement'
        };
        return typeMap[type] || type;
    };

    return (
        <div className="flex items-start flex-col bg-eerie-black border border-zinc-700 rounded-2xl">
            <div className="w-full flex items-center gap-8 px-6 py-5 border-zinc-700 border-b"> 
                {/* Reference ID */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Reference ID</p>
                    <p className="text-white text-sm font-mono">{transaction.reference_id || 'N/A'}</p>
                </div>

                {/* Account ID */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Account ID</p>
                    <p className="text-white text-sm">{transaction.account_id || 'N/A'}</p>
                </div>

                {/* Account Number */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Account Number</p>
                    <p className="text-white text-sm">{transaction.account_number || 'N/A'}</p>
                </div>

                {/* UTR (if available) */}
                {transaction.utr && (
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">UTR</p>
                        <p className="text-white text-sm font-mono">{transaction.utr}</p>
                    </div>
                )}

                {/* Transaction Type */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Type</p>
                    <p className="text-white font-medium text-sm">{getTypeLabel(transaction.type)}</p>
                </div>

                {/* Entry Type */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Entry Type</p>
                    <p className={`font-medium uppercase text-sm ${getEntryTypeClass(transaction.entry_type)}`}>
                        {transaction.entry_type}
                    </p>
                </div>

                {/* Status */}
                <div className="ml-auto">
                    <p className="text-xs text-zinc-500 mb-2 text-right">Status</p>
                    <button 
                        className={`px-2 py-1 rounded-lg text-xs font-medium uppercase ${getStatusClass(transaction.status)}`}
                    >
                        {transaction.status}
                    </button>
                </div>
            </div>

            <div className="w-full flex items-center gap-8 px-6 py-5">
                {/* Amount */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Amount</p>
                    <p className="text-white font-bold text-base">{formatCurrency(transaction.amount)}</p>
                </div>

                {/* Balance */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Balance</p>
                    <p className="text-white font-medium text-base">{formatCurrency(transaction.balance)}</p>
                </div>

                {/* Meta Remarks - Amount (if available) */}
                {transaction.meta_remarks.details[0].amount && (
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Meta Remarks (Amount)</p>
                        <p className="text-white text-base">{formatCurrency(transaction.meta_remarks.details[0].amount)}</p>
                    </div>
                )}

                {/* Meta Remarks - Title (if available) */}
                {transaction.meta_remarks.details[0].title && (
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Meta Remarks (Title)</p>
                        <p className="text-white text-base">{transaction.meta_remarks.details[0].title}</p>
                    </div>
                )}

                {/* Remarks */}
                {transaction.remarks && (
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Remarks</p>
                        <p className="text-white text-base">{transaction.remarks}</p>
                    </div>
                )}

                {/* Date & Time */}
                <div>
                    <p className="text-xs text-zinc-500 mb-1">Date & Time</p>
                    <p className="text-white text-base">
                        {formatDate(transaction.created_at)} : {formatTime(transaction.created_at)}
                    </p>
                </div>
                
                {/* Refund button for UPI deposit transactions */}
                {transaction.type === 'upi_instant_deposit' && 
                 (transaction.status?.toLowerCase() === 'success') && (
                    <div className="ml-auto">
                        <button
                            onClick={() => setShowRefundModal(true)}
                            disabled={isRefunding}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                        >
                            {isRefunding ? 'Processing...' : 'REFUND'}
                        </button>
                    </div>
                )}
            </div>
            
            {/* Refund Confirmation Modal */}
            {showRefundModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-charleston-green border border-zinc-700 rounded-lg p-6 max-w-sm w-full mx-4">
                        <h3 className="text-white text-lg font-medium mb-3">Confirm Refund</h3>
                        <p className="text-zinc-400 text-sm mb-2">
                            Are you sure you want to refund this UPI deposit transaction?
                        </p>
                        <p className="text-zinc-300 text-sm mb-6">
                            <strong>Reference ID:</strong> {transaction.reference_id}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowRefundModal(false)}
                                disabled={isRefunding}
                                className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm hover:bg-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    setIsRefunding(true);
                                    try {
                                        const result = await dispatch(adminHandlers.refundTransactionHandler(transaction.id));
                                        setShowRefundModal(false);
                                        
                                        // Show notification
                                        setNotification({
                                            success: result.success,
                                            message: result.message,
                                            task_id: result.task_id
                                        });
                                        
                                        // Refresh transactions list if successful
                                        if (result.success && onRefresh) {
                                            setTimeout(() => {
                                                onRefresh();
                                            }, 2000);
                                        }
                                    } catch (error) {
                                        console.error('Refund failed:', error);
                                        setShowRefundModal(false);
                                        setNotification({
                                            success: false,
                                            message: 'Failed to process refund request',
                                            task_id: null
                                        });
                                    } finally {
                                        setIsRefunding(false);
                                    }
                                }}
                                disabled={isRefunding}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isRefunding ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    'Confirm Refund'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Toast Notification */}
            {notification && (
                <>
                <style jsx>{`
                    @keyframes slideInRight {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    @keyframes slideOutRight {
                        from {
                            transform: translateX(0);
                            opacity: 1;
                        }
                        to {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                    }
                    .animate-slide-in {
                        animation: slideInRight 0.3s ease-out;
                    }
                `}</style>
                <div className="fixed top-4 right-4 z-50 animate-slide-in" style={{animation: 'slideInRight 0.3s ease-out'}}>
                    <div className={`bg-charleston-green border ${notification.success ? 'border-green-600' : 'border-red-600'} rounded-lg p-4 shadow-xl min-w-[400px] max-w-lg`}>
                        <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${notification.success ? 'bg-green-600' : 'bg-red-600'}`}>
                                {notification.success ? (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </div>
                            <div className="flex-1">
                                <p className={`font-medium text-sm ${notification.success ? 'text-green-500' : 'text-red-500'}`}>
                                    {notification.success ? 'Refund Initiated' : 'Refund Failed'}
                                </p>
                                <p className="text-zinc-300 text-sm mt-1">
                                    {notification.message}
                                </p>
                                {notification.task_id && (
                                    <p className="text-zinc-400 text-xs mt-2">
                                        Task ID: {notification.task_id}
                                    </p>
                                )}
                            </div>
                            <button 
                                onClick={() => setNotification(null)}
                                className="flex-shrink-0 text-zinc-500 hover:text-white transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                </>
            )}
        </div>
    );
};

export default TransactionCard;